/**
 * Selector logic for the research-DAG builder.
 *
 * A **selector** is one LLM decision on one source node's answer that picks any
 * subset of its candidate targets. Every edge carrying the same
 * `condition.selector_id` belongs to that one decision — so two edges sharing an
 * id are "pick some of these two", not two independent yes/no gates.
 *
 * Two rules the backend guarantees and the builder must therefore preserve:
 *  - all edges in a selector share the same `from` node;
 *  - all edges in a selector share the same `expression`.
 *
 * A node may have any number of conditional parents; it runs if *any* selector
 * picked it. That includes the synthesizer: every edge into it may be gated, in
 * which case a run where no selector picks anything reaches the memo with no
 * inputs. The builder allows that shape and warns about it rather than
 * refusing it.
 *
 * Pure functions over `{ _uid, from, to, condition }` edge objects, so they are
 * testable without the canvas (see ./workflowSelectors.test.js).
 */

/**
 * The only `condition.kind` the runtime emits or accepts. A selector is an LLM
 * that reads the source node's answer once and picks any subset of its
 * candidate targets — so every edge sharing a `selector_id` is one decision,
 * not several independent yes/no gates.
 */
export const CONDITION_KIND = "llm_multi_select";

export const CONDITION_KIND_HINT =
  "An LLM reads the source node's answer and picks which of this selector's targets receive it. " +
  "Targets it does not pick are skipped for that run.";

/** A fresh selector id, unique within the template. */
export const makeSelectorId = (used) => {
  let n = 1;
  while (used.has(`sel_${n}`)) n++;
  return `sel_${n}`;
};

/** Every selector in the graph: `selector_id` → the edges it decides between. */
export const selectorGroups = (edges) => {
  const map = new Map();
  for (const e of edges) {
    if (!e.condition) continue;
    const id = e.condition.selector_id;
    if (!map.has(id)) map.set(id, []);
    map.get(id).push(e);
  }
  return map;
};

/** Selectors driven by `from`, in a stable order — used for the accent hues. */
export const selectorsFrom = (edges, from) => {
  const seen = [];
  for (const e of edges) {
    if (e.condition && e.from === from && !seen.includes(e.condition.selector_id))
      seen.push(e.condition.selector_id);
  }
  return seen;
};

/**
 * What an edge's label reads — also what its width is budgeted from. Conditional
 * edges are labelled by their selector, not per edge: the whole group shares one
 * expression, so repeating it on every edge is noise (§3 of the graph spec).
 */
export const edgeLabelText = (e, groupSize = 1) => {
  if (!e.condition) return "always";
  const rule = e.condition.expression?.trim() || "— set rule";
  return groupSize > 1 ? `PICK ${rule}` : `IF ${rule}`;
};

/**
 * Flips one edge between conditional and unconditional.
 *
 * Turning an edge ON either joins it to an existing selector on the same source
 * (`joinSelectorId`) or opens a new one-edge selector. A node may now have any
 * number of conditional parents — it runs if *any* selector picked it — and the
 * synthesizer's inputs may all be gated too. Gating every edge into the
 * synthesizer is allowed here: it only means a run where no selector picks
 * anything reaches the memo with no inputs, which the builder surfaces as a
 * warning rather than blocking (see `issues.warnings` in WorkflowTemplates).
 *
 * Turning an edge OFF drops it out of its selector; a selector left with no
 * edges simply ceases to exist.
 */
export const applyConditional = (edges, edgeUid, on, { joinSelectorId = null } = {}) => {
  const target = edges.find(e => e._uid === edgeUid);
  if (!target) return edges;

  if (on) {
    if (target.condition) return edges;

    /*
     * Which selector this edge joins:
     *
     *  1. the one asked for, if any;
     *  2. otherwise the selector already driven by this same source node — a
     *     second conditional edge out of a node is nearly always part of the
     *     same decision ("pick which of these follow-ups to run"), so defaulting
     *     to a fresh selector each time forced the author to merge them by hand
     *     every single time;
     *  3. otherwise a brand-new one, for the node's first conditional edge.
     *
     * Splitting a decision apart is the rarer intent, and stays one click away
     * via "New selector" on the edge.
     */
    const sibling = joinSelectorId
      ? edges.find(e => e.condition?.selector_id === joinSelectorId && e.from === target.from)
      : edges.find(e => e.condition && e.from === target.from);
    const selectorId = sibling
      ? sibling.condition.selector_id
      : makeSelectorId(new Set([...selectorGroups(edges).keys()]));

    return edges.map(e => e._uid === edgeUid
      ? { ...e, condition: { kind: CONDITION_KIND, selector_id: selectorId, expression: sibling?.condition.expression || "" } }
      : e);
  }

  return edges.map(e => (e._uid === edgeUid && e.condition ? { ...e, condition: null } : e));
};

/**
 * Edits one selector's rule. The expression belongs to the selector, so the
 * edit lands on every edge in the group at once — they are one decision.
 */
export const applySelectorExpression = (edges, selectorId, expression) =>
  edges.map(e => (e.condition?.selector_id === selectorId
    ? { ...e, condition: { ...e.condition, expression } }
    : e));

/**
 * Moves one edge into another selector (or, with `null`, into a brand-new one).
 *
 * "New selector" on an edge that is *already alone* in its group is a no-op the
 * user cannot see — it would swap one single-edge selector for another. Rather
 * than mint a pointless id, leave the edge where it is; it is already its own
 * decision. That is what made the option look broken.
 */
export const applySelectorId = (edges, edgeUid, selectorId) => {
  const target = edges.find(e => e._uid === edgeUid);
  if (!target?.condition) return edges;

  if (!selectorId) {
    const groupSize = edges.filter(
      e => e.condition?.selector_id === target.condition.selector_id).length;
    if (groupSize <= 1) return edges;   // already a selector of its own
  }

  const id = selectorId || makeSelectorId(new Set([...selectorGroups(edges).keys()]));
  const sibling = edges.find(e =>
    e._uid !== edgeUid && e.condition?.selector_id === id && e.from === target.from);
  return edges.map(e => e._uid === edgeUid
    ? {
        ...e,
        condition: {
          ...e.condition,
          selector_id: id,
          // a brand-new selector is a brand-new decision — it must not inherit
          // the rule of the group the edge just left
          expression: sibling ? sibling.condition.expression : (selectorId ? e.condition.expression : ""),
        },
      }
    : e);
};
